/**
 * Moving through a queue of claims.
 *
 * "A decision panel opens over the queue and keeps it behind: approving returns
 * to the list with the next claim already selected." The advance is the whole
 * point of the shape — deciding twenty claims should cost twenty decisions, not
 * twenty round trips through a list.
 *
 * What "next" means is the fiddly part, and it is decided here rather than in
 * the component so it can be reasoned about: the claim just decided is gone
 * from the pending list by the time we look, so "the one after it" has to be
 * worked out from where it *was*.
 */

/**
 * The claim to open after deciding `decidedId`.
 *
 * `before` is the list as it was when the panel opened; `after` is what remains
 * once the decided claim has left it. Taking the index from the old list and
 * reading it in the new one lands on what was underneath — which is what the
 * eye expects, because that row moves up into the space.
 *
 * Returns null when the queue is empty, which is the signal to close the panel
 * rather than to open nothing.
 */
export const nextAfterDecision = (before = [], after = [], decidedId, getId = (c) => c?._id) => {
  const remaining = (after || []).filter((c) => String(getId(c)) !== String(decidedId));
  if (!remaining.length) return null;

  const wasAt = (before || []).findIndex((c) => String(getId(c)) === String(decidedId));
  if (wasAt < 0) return remaining[0];

  // The row that moved up into the vacated position, or the new last one if the
  // decided claim was at the bottom.
  return remaining[Math.min(wasAt, remaining.length - 1)];
};

/**
 * Where in the queue this claim sits, for "3 of 9" in the panel.
 * One-indexed, and 0 when it is not in the list at all.
 */
export const positionOf = (list = [], id, getId = (c) => c?._id) => {
  const i = (list || []).findIndex((c) => String(getId(c)) === String(id));
  return i < 0 ? 0 : i + 1;
};

/**
 * Only a pending claim is a decision. An approved one opened from the list is
 * a record being read, and offering Approve on it would be offering to do
 * something that has already happened.
 */
export const isDecidable = (claim) => claim?.status === "pending";

/**
 * Whether the queue can advance at all — used to word the primary action.
 * "Approve and next" promises something that is not there on the last one.
 */
export const hasNext = (before = [], after = [], decidedId, getId) =>
  nextAfterDecision(before, after, decidedId, getId) != null;
